import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Eye, Lock } from "lucide-react";
import { useTribeMembers } from "@/hooks/useTribeMembers";
import { participantRefKey, type ParticipantRef } from "@/types/participant";

interface VisibilityReadoutProps {
  participants: ParticipantRef[];
  /** Live mirror of the "Share with tagged people" toggle. When false, the
   *  memory's audience is 'members' — tagged non-members (contributors +
   *  tagged children's future promoted accounts) lose access and should
   *  disappear from the readout immediately. */
  isSharedWithTagged: boolean;
  /** Children tagged on the memory. Listed under "Tagged people" when the
   *  toggle is ON. Kids who already have their own Kindle-kid login see
   *  the memory immediately via the `memories_child_select` RLS branch
   *  (20260424140000); kids without a login will see it once promoted.
   *  `hasActiveChildAccount` is resolved by `useChildren` and drives the
   *  present-tense vs. future-tense framing per row. */
  taggedChildren?: Array<{
    id: string;
    name: string;
    hasActiveChildAccount: boolean;
  }>;
}

// Read-only mirror of who will see the memory once saved.
// Rules (all enforced at RLS today):
//   - Owner + all role='member' space members: always see it.
//   - Contributors: see it only if tagged AND audience='space'.
//   - Tagged children with a Kindle-kid account: see it today if
//     audience='space' (via memories_child_select branch 20260424140000).
//   - Tagged children without an account: will see it once promoted.
// "Tagged non-members" = tagged contributors + tagged children. Owners are
// always the creator — they never belong on this list.
export default function VisibilityReadout({
  participants,
  isSharedWithTagged,
  taggedChildren = [],
}: VisibilityReadoutProps) {
  const { t } = useTranslation();
  const { owner, coparents, contributors, isLoading } = useTribeMembers();
  const [open, setOpen] = useState(false);

  if (isLoading || !owner) return null;

  const taggedKeys = new Set(participants.map(participantRefKey));
  const taggedContributors = contributors.filter((c) =>
    taggedKeys.has(
      participantRefKey({
        kind: "connection",
        connectionId: c.connectionId,
        profileId: c.profileId,
        role: c.role,
        name: c.name,
      }),
    ),
  );

  // Tagged "shareables" — people whose access depends on the toggle.
  // Contributors and promoted-kid accounts see today; unpromoted kids will
  // see the memory once they have their own login.
  const taggedShareables: Array<{
    key: string;
    name: string;
    isChild: boolean;
    hasAccount: boolean;
  }> = [
    ...taggedContributors.map((c) => ({
      key: `c:${c.connectionId}`,
      name: c.name,
      isChild: false,
      hasAccount: true,
    })),
    ...taggedChildren.map((c) => ({
      key: `k:${c.id}`,
      name: c.name,
      isChild: true,
      hasAccount: c.hasActiveChildAccount,
    })),
  ];

  const effectivelyVisibleTagged = isSharedWithTagged ? taggedShareables : [];
  const familyLabel = coparents.length > 0 ? t("visibility.fullFamily") : t("visibility.onlyYou");

  // Collapsed summary — shows family + tagged names when they have access.
  const summary =
    effectivelyVisibleTagged.length === 0
      ? familyLabel
      : effectivelyVisibleTagged.length === 1
        ? `${familyLabel} & ${effectivelyVisibleTagged[0].name}`
        : effectivelyVisibleTagged.length === 2
          ? `${familyLabel} & ${effectivelyVisibleTagged[0].name}, ${effectivelyVisibleTagged[1].name}`
          : `${familyLabel} & ${effectivelyVisibleTagged[0].name} + ${effectivelyVisibleTagged.length - 1} others`;

  return (
    <div className="border-t border-border/40 pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        aria-expanded={open}
      >
        <Eye className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left truncate">
          <span className="font-medium">Visible to:</span> {summary}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 shrink-0 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : undefined }}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-3 pl-5 text-[12px]">
          {/* Full family — always listed */}
          <div>
            <p className="font-medium text-foreground">
              {familyLabel}
              <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                (Always has access)
              </span>
            </p>
            <ul className="mt-1 space-y-0.5 text-muted-foreground">
              <li>{t("visibility.youOwner", { owner: owner.nickname })}</li>
              {coparents.map((cp) => (
                <li key={cp.connectionId}>{cp.name}</li>
              ))}
            </ul>
          </div>

          {/* Tagged people — only when toggle is ON and there are any */}
          {effectivelyVisibleTagged.length > 0 && (
            <div>
              <p className="font-medium text-foreground">
                Tagged people
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                  (Has access because they are tagged)
                </span>
              </p>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {effectivelyVisibleTagged.map((s) => (
                  <li key={s.key}>
                    {s.name}
                    {s.isChild && !s.hasAccount && (
                      <span className="ml-1.5 text-[11px] text-muted-foreground/80">
                        (when they have their own account)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer — static lock reminder */}
          <div className="flex items-center gap-1.5 border-t border-border/40 pt-2 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3 shrink-0" />
            <span>{t("help.visibilityPrivate")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
